import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { CardReconciliationSummary, ReconciliationMaster, ReconciliationRun } from "@/api/types";
import { useWorkspace } from "@/app/workspace";
import { CardBrandsOverview, useCardBrandOverview, type CardOverviewStage } from "@/components/CardBrandsOverview";
import { CardWorkspace } from "@/components/CardWorkspace";
import { useOptionalConfirmDialog } from "@/components/ConfirmDialog";
import { masterSearch } from "@/components/DomainContext";
import { DomainShell, DownloadIcon, useDomainWorkflow } from "@/components/DomainShell";
import { ImportDetail, type ImportWorkspaceConfig } from "@/components/ImportWorkspace";
import { useToast } from "@/components/Toast";
import { money } from "@/components/format";
import { ReviewWorkspace } from "@/components/WorkflowViews";

const importConfig: ImportWorkspaceConfig = { domain: "CARD", accountKind: "CARD", apiBase: "/cards", basePath: "/tarjetas", noun: "tarjetas" };
type View = "reconciliation" | "data" | "review";

const statusCopy: Record<CardReconciliationSummary["controls"][number]["status"], { label: string; detail: string }> = {
  MATCHED: { label: "Control conciliado", detail: "El Total presentado coincide exactamente con ATA." },
  DIFFERENCE: { label: "Diferencia a revisar", detail: "Los ATA permanecen pendientes hasta corregir la diferencia." },
  MISSING_TOTALS: { label: "Faltan totales", detail: "La liquidación no publicó totales documentales autoritativos." },
  MISSING_ATA: { label: "Falta ATA", detail: "No se encontró una acreditación ATA válida en el Haber." },
};

function CardMonthlySummary({ cardBrandId }: { cardBrandId: string }) {
  const { hotelId, period } = useWorkspace();
  const summary = useQuery({
    queryKey: ["card-review-summary", hotelId, period, cardBrandId],
    queryFn: (): Promise<CardReconciliationSummary> => responseData(apiClient.GET("/api/v1/cards/review/summary", {
      params: { query: { hotelId, period, cardBrandId } },
    })),
    enabled: Boolean(hotelId && cardBrandId),
  });

  if (summary.isPending) return <div className="bank-accounts-overview__loading" role="status">Calculando el cierre mensual…</div>;
  if (summary.isError) return <div className="message message--error">No se pudo cargar el resumen mensual.</div>;
  if (!summary.data.controls.length) return <section className="card-monthly-empty"><strong>Sin control mensual disponible</strong><span>Confirmá una liquidación compatible y el Libro Mayor antes de conciliar.</span></section>;

  return <section className="card-monthly-summary" aria-label="Resumen mensual de Tarjetas">
    {summary.data.controls.map((control) => {
      const credit = control.funding.filter((item) => item.cardFunding === "CREDIT").reduce((total, item) => total + Number(item.presentedTotal), 0);
      const debit = control.funding.filter((item) => item.cardFunding === "DEBIT").reduce((total, item) => total + Number(item.presentedTotal), 0);
      const copy = statusCopy[control.status];
      const missingMessages = [
        control.status === "MISSING_TOTALS" ? "Confirmá una liquidación compatible que informe Total presentado y Neto de pagos." : "",
        control.status === "MISSING_ATA" ? "Cargá o corregí el asiento ATA en el Haber del Libro Mayor." : "",
      ].filter(Boolean);
      return <section className={`bank-balance-bridge card-monthly-control card-monthly-control--${control.status.toLowerCase()}`} key={`${control.cardBrandId}:${control.currency}`}>
        <header>
          <div><small>Cierre mensual · {summary.data.period}</small><h2>{control.cardBrandName} · {control.currency}</h2></div>
          <div className="card-monthly-status"><strong>{copy.label}</strong><small>{copy.detail}</small></div>
        </header>
        <div className="bank-balance-equation card-monthly-equation" aria-label="Composición del total presentado">
          <article><small>Crédito presentado</small><strong>{money(credit, control.currency)}</strong></article>
          <span className="bank-balance-operator" aria-hidden="true">+</span>
          <article><small>Débito presentado</small><strong>{money(debit, control.currency)}</strong></article>
          <span className="bank-balance-operator" aria-hidden="true">=</span>
          <article className="bank-balance-result"><small>Total presentado</small><strong>{money(control.presentedTotal, control.currency)}</strong></article>
        </div>
        <div className="bank-balance-reconciliation card-monthly-ledger">
          <article><small>Neto de pagos</small><strong>{money(control.netPaymentsTotal, control.currency)}</strong><span>Importe neto informado por la liquidación</span></article>
          <article><small>Deducciones</small><strong>{money(control.deductionsTotal, control.currency)}</strong><span>Total presentado − neto de pagos</span></article>
          <article><small>ATA en Haber</small><strong>{money(control.ataTotal, control.currency)}</strong><span>{control.ataEntryCount} {control.ataEntryCount === 1 ? "asiento" : "asientos"} encontrado{control.ataEntryCount === 1 ? "" : "s"}</span></article>
          <article className={`bank-balance-difference${Number(control.difference) === 0 ? "" : " has-difference"}`}><small>Diferencia</small><strong>{money(control.difference, control.currency)}</strong><span>Total presentado − ATA</span></article>
        </div>
        {missingMessages.length > 0 && <div className="bank-balance-missing" role="status"><b>Datos incompletos</b><ul>{missingMessages.map((message) => <li key={message}>{message}</li>)}</ul></div>}
      </section>;
    })}
  </section>;
}

export function CardsPage({ view = "data" }: { view?: View }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useOptionalConfirmDialog();
  const navigate = useNavigate();
  const { hotelId, period } = useWorkspace();
  const [params] = useSearchParams();
  const masterId = params.get("masterId") ?? "";
  const aggregateScope = !masterId;
  const contextSearch = masterSearch(masterId);
  const workflow = useDomainWorkflow(importConfig);
  const hasWorkflowSources = Boolean(workflow.data?.sources.length);
  const brandOverview = useCardBrandOverview(hotelId, period, masterId, true);
  const brands = useQuery({
    queryKey: ["reconciliation-master-options", "CARD"],
    queryFn: async () => {
      const result = await responseData(apiClient.GET("/api/v1/cards/catalog"));
      return result.map((item): ReconciliationMaster => ({ ...item, domain: "CARD", aliases: [], sortOrder: 0 }));
    },
  });
  const run = useQuery({
    queryKey: ["run", "card", hotelId, period],
    queryFn: async (): Promise<ReconciliationRun | null> => (await responseData(apiClient.GET("/api/v1/cards/reconciliations/latest", { params: { query: { hotelId, period } } }))) ?? null,
    enabled: Boolean(hotelId) && (view === "reconciliation" || view === "review" || hasWorkflowSources),
  });
  const selectedBrand = brands.data?.find((brand) => brand.id === masterId);
  const title = selectedBrand ? `Tarjeta ${selectedBrand.name}` : "Tarjetas";
  const overviewStage: CardOverviewStage = view === "data" ? "validate" : view === "review" ? "review" : "reconcile";
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ["run", "card", hotelId, period] }),
    queryClient.invalidateQueries({ queryKey: ["workflow", "CARD"] }),
    queryClient.invalidateQueries({ queryKey: ["card-brand-overview", hotelId, period] }),
    queryClient.invalidateQueries({ queryKey: ["card-settlements"] }),
    queryClient.invalidateQueries({ queryKey: ["card-ledger"] }),
    queryClient.invalidateQueries({ queryKey: ["review-groups"] }),
    queryClient.invalidateQueries({ queryKey: ["card-review-summary", hotelId, period] }),
  ]);
  const execute = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/cards/reconciliations", { body: { hotelId, period, cardBrandId: masterId || undefined } })),
    onSuccess: async (result) => {
      const processed = result.processedBrands?.length ?? (masterId ? 1 : 0);
      const skipped = result.skippedBrands?.length ?? 0;
      toast.success(aggregateScope ? `${processed} marca${processed === 1 ? "" : "s"} conciliada${processed === 1 ? "" : "s"}${skipped ? ` · ${skipped} omitida${skipped === 1 ? "" : "s"}` : ""}.` : "Conciliación de tarjetas ejecutada.");
      await refresh();
      navigate(`/tarjetas/revision${contextSearch}`);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo ejecutar la conciliación de tarjetas." }),
  });
  const reconcile = async () => {
    if (!aggregateScope) { execute.mutate(); return; }
    const brandsInScope = brandOverview.data?.brands ?? [];
    const ready = brandsInScope.filter((brand) => brand.stages.validate === "COMPLETE");
    const skipped = brandsInScope.filter((brand) => brand.stages.validate !== "COMPLETE");
    const readyNames = ready.map((brand) => brand.brandName).join(", ");
    const skippedNames = skipped.map((brand) => {
      const missing = [brand.loads.liquidationStatus ? "" : "Liquidación", brand.loads.ledgerStatus ? "" : "Libro Mayor"].filter(Boolean).join(" y ");
      return `${brand.brandName}${missing ? ` (${missing})` : " (requiere validación)"}`;
    }).join(", ");
    const accepted = confirm ? await confirm({
      title: `Conciliar ${ready.length} marca${ready.length === 1 ? "" : "s"} lista${ready.length === 1 ? "" : "s"}`,
      description: `Se procesarán: ${readyNames || "ninguna"}.${skippedNames ? ` Se omitirán: ${skippedNames}.` : ""}`,
      confirmLabel: "Ejecutar conciliación",
    }) : false;
    if (accepted) execute.mutate();
  };
  const error = brandOverview.error ?? run.error;

  return <DomainShell
    eyebrow="Adquirentes contra Libro Mayor"
    title={title}
    description={selectedBrand
      ? "Validá los movimientos, ejecutá el cruce y cerrá el mes contra las acreditaciones ATA."
      : "Cargá Liquidación y Libro Mayor por marca, validá los pendientes y ejecutá el cierre mensual."}
    basePath="/tarjetas"
    dataPath="liquidaciones"
    importConfig={importConfig}
    compactContext
    onReconcile={reconcile}
    reconciling={execute.isPending}
    actions={hasWorkflowSources ? <a className="context-action" href={`/api/v1/cards/reports/liquidations.xlsx?hotelId=${encodeURIComponent(hotelId)}&year=${encodeURIComponent(period.slice(0, 4))}`}><DownloadIcon/><span>Exportar Excel</span></a> : null}
  >
    {execute.isPending && <section className="bank-rules-processing" role="status" aria-live="polite" aria-label="Conciliación de tarjetas en proceso">
      <div className="bank-rules-processing__match" aria-hidden="true"><span className="bank-rules-processing__ledger"><i/><i/><i/></span><b>↔</b><span className="bank-rules-processing__ledger"><i/><i/><i/></span></div>
      <div className="bank-rules-processing__copy"><small>Conciliación en proceso</small><strong>Ejecutando reglas y control mensual</strong><span>Comparamos cupones contra FAC y el Total presentado contra los asientos ATA.</span></div>
      <span className="bank-rules-processing__track" aria-hidden="true"><i/></span>
    </section>}
    {error && <div className="message message--error">{error instanceof Error ? error.message : "No se pudo cargar el escritorio."}</div>}
    {aggregateScope ? brandOverview.data
      ? <CardBrandsOverview data={brandOverview.data} activeStage={overviewStage}/>
      : brandOverview.isPending ? <div className="bank-accounts-overview__loading" role="status">Armando la mesa de marcas…</div> : null
      : view === "review" ? <>
        <CardMonthlySummary cardBrandId={masterId}/>
        <ReviewWorkspace config={importConfig} workflow={workflow.data} run={run.data}/>
      </> : <CardWorkspace view={view}/>
    }
  </DomainShell>;
}

export function CardsImportDetailPage() { return <DomainShell eyebrow="Adquirentes contra Libro Mayor" title="Tarjetas" description="Detalle y validación de una carga de tarjetas." basePath="/tarjetas" dataPath="liquidaciones" importConfig={importConfig}><ImportDetail config={importConfig}/></DomainShell>; }
